/**
 * The one shape for "a condition, and what is said when it holds" — D138.
 *
 * @remarks
 * D135 gave items `closeRefusal` and `openRefusal` as `{ narratives, when? }`;
 * D138 gives exits `reveal` as `{ narratives, when }`. Three fields with one
 * meaning are one shape, not three; the only difference is whether `when`
 * may be omitted (a refusal that always applies is legitimate — a bench that
 * will never close; a reveal with no condition is an exit that was never
 * hidden, which is dead content). So the base carries the required form, and
 * the refusal is the base with `when` relaxed.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { narrativeLinesSchema } from './transcript.js';

/** The base: lines spoken when — and only when — the condition holds. */
const spokenWhenBaseSchema = z.strictObject({
	narratives: narrativeLinesSchema,
	when: conditionSchema,
});

/** Lines under a REQUIRED condition — the reveal's shape (D138). */
export const spokenWhenSchema = spokenWhenBaseSchema.readonly();

/** Lines spoken under a condition. */
export type SpokenWhen = z.infer<typeof spokenWhenSchema>;

/** Lines under an OPTIONAL condition — the refusal's shape (D135). */
export const spokenWhenOptionalSchema = spokenWhenBaseSchema.extend({ when: conditionSchema.optional() }).readonly();
